import { current } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import queryString from 'query-string';

import { TrainingSetItemStatus, TrainingStatus } from '../../shared/constants';
import { IAppFile } from '../../types/interfaces/app-file';
import { IExercise } from '../../types/interfaces/exercise';
import { ITraining } from '../../types/interfaces/training';
import { ITrainingActivityType } from '../../types/interfaces/training-activity-type';
import { ITrainingSet } from '../../types/interfaces/training-set';
import { ITrainingSetItem } from '../../types/interfaces/training-set-item';
import {
  ApiResponseCache,
  getStartedTrainingCacheKey,
} from '../api-response-cache';
import { HTTP_METHOD, REDUX_CACHE, TRAININGS } from '../constants';
import * as endpoints from '../endpoints';
import {
  TransformedErrorResponseWithNotification,
  getBaseQuery,
  getListProvidesTags,
  getUrlParamsString,
} from '../generic';
import {
  removeStartedTrainingCache,
  syncStartedTrainingSnapshotFromTraining,
} from '../started-training-snapshot';
import { IPutTrainingSet } from './exercises/interfaces';
import { mergeTrainingSetsForBulkUpdate } from './trainings-api-utils';

export const trainingsApi = createApi({
  reducerPath: 'trainingsApi',
  tagTypes: [
    TRAININGS.TRAININGS,
    TRAININGS.TRAININGS_LAZY,
    TRAININGS.TRAINING_ITEMS,
    TRAININGS.AVAILABLE,
    TRAININGS.CREATED_FOR_OTHERS,
    TRAININGS.EXERCISES,
    TRAININGS.TRAININGS_BY_DAY,
    TRAININGS.TRAININGS_BY_PERIOD,
    TRAININGS.TRAININGS_FOR_ANALYTIC,
    TRAININGS.TRAINING_ACTIVITY_TYPES,
    REDUX_CACHE.ABORT_INVALIDATION,
    REDUX_CACHE.LIST,
  ],
  baseQuery: getBaseQuery(),
  // global configuration for the api
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getUserTrainingsApi: builder.query<
      ITraining[],
      {
        includeItems?: boolean;
      } | void
    >({
      keepUnusedDataFor: 3600,
      query(args) {
        const qs = args ? queryString.stringify(args) : '';

        return {
          url: `${endpoints.Training.ROOT}${qs ? '?' + qs : ''}`,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, TRAININGS.TRAININGS),
      ...TransformedErrorResponseWithNotification(),
    }),
    getUserTrainingsLazyApi: builder.query<
      ITraining[],
      {
        pageSize: number;
        page: number;
        searchValue?: string;
      }
    >({
      keepUnusedDataFor: 3600,
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `userTrainingsLazy-${queryArgs.page}-${queryArgs.pageSize}-${queryArgs.searchValue ?? ''}`;
      },
      query({ pageSize, page, searchValue = '' }) {
        const params: Record<string, string | number> = {
          pageSize,
          page,
          searchValue,
        };
        const qs = queryString.stringify(params);
        return {
          url: `${endpoints.Training.ROOT}?${qs}`,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result, _, arg) => [
        {
          type: TRAININGS.TRAININGS_LAZY,
          id:
            'trainingsLazy_' +
            arg.page +
            '_' +
            arg.pageSize +
            '_' +
            (arg.searchValue ?? ''),
        },
      ],
    }),
    getAvailableUserTrainingsApi: builder.query<
      ITraining[],
      { searchValue: string }
    >({
      keepUnusedDataFor: 3600,
      query({ searchValue }) {
        const qs = getUrlParamsString({ searchValue });
        return {
          url: `${endpoints.Training.AVAILABLE}?${qs}`,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, TRAININGS.AVAILABLE),
    }),
    getCreatedForOthersUserTrainingsApi: builder.query<
      ITraining[],
      { searchValue: string }
    >({
      keepUnusedDataFor: 3600,
      query({ searchValue }) {
        const qs = getUrlParamsString({ searchValue });
        return {
          url: `${endpoints.Training.CREATED_FOR_OTHERS}?${qs}`,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, TRAININGS.CREATED_FOR_OTHERS),
    }),
    getTrainingsByDayApi: builder.query<
      ITraining[],
      { day: string; statuses?: TrainingStatus[] }
    >({
      keepUnusedDataFor: 3600,
      query({ day, statuses = [] }) {
        return {
          url: endpoints.Training.DAY(day, statuses),
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result, _, arg) => [
        ...getListProvidesTags(result, TRAININGS.TRAININGS_BY_DAY),
        {
          type: TRAININGS.TRAININGS_BY_DAY,
          id: `${arg.day}_${arg.statuses?.join(',') ?? 'all'}`,
        },
      ],
      ...TransformedErrorResponseWithNotification(),
    }),
    getTrainingsByPeriodApi: builder.query<
      ITraining[],
      { period: string; finished?: boolean }
    >({
      keepUnusedDataFor: 3600,
      query({ period, finished = true }) {
        return {
          url: endpoints.Training.PERIOD(period, finished),
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result, _, arg) => [
        ...getListProvidesTags(result, TRAININGS.TRAININGS_BY_PERIOD),
        {
          type: TRAININGS.TRAININGS_BY_PERIOD,
          id: `period_${arg.period}_${arg.finished ?? true}`,
        },
      ],
      ...TransformedErrorResponseWithNotification(),
    }),
    getTrainingsForAnalyticApi: builder.query<
      ITraining[],
      { month: number; year: number }
    >({
      keepUnusedDataFor: 3600,
      query({ month, year }) {
        const qs = queryString.stringify({ month, year });
        return {
          url: `${endpoints.Training.TRAININGS_FOR_ANALYTIC}?${qs}`,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result, _, arg) => [
        ...getListProvidesTags(result, TRAININGS.TRAININGS_FOR_ANALYTIC),
        {
          type: TRAININGS.TRAININGS_FOR_ANALYTIC,
          id: `${arg.month}_${arg.year}`,
        },
      ],
      ...TransformedErrorResponseWithNotification(),
    }),
    getTrainingActivityTypesApi: builder.query<ITrainingActivityType[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.Training.ACTIVITY_TYPES,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, TRAININGS.TRAINING_ACTIVITY_TYPES),
      ...TransformedErrorResponseWithNotification(),
    }),
    getTrainingByIdApi: builder.query<ITraining, string>({
      keepUnusedDataFor: 3600,
      query(id) {
        return {
          url: `${endpoints.Training.ROOT}/${id}`,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result, error, id) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [{ type: TRAININGS.TRAININGS, id }];
        }
      },
      transformResponse(response: ITraining) {
        syncStartedTrainingSnapshotFromTraining(response);
        return response;
      },
      transformErrorResponse(response: unknown, _meta, arg: string) {
        if (
          arg &&
          (response as { status?: string })?.status !== 'FETCH_ERROR'
        ) {
          removeStartedTrainingCache(arg);
        }
        return response;
      },
      // ...TransformedErrorResponseWithNotification(),
    }),
    getTrainingExercisesApi: builder.query<IExercise[], string>({
      keepUnusedDataFor: 3600,
      query(itemId) {
        return {
          url: endpoints.Training.EXERCISES(itemId),
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result, error, id) => {
        return [{ type: TRAININGS.EXERCISES, id }];
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    getTrainingExercisesDetailedApi: builder.query<IExercise[], string>({
      keepUnusedDataFor: 3600,
      query(itemId) {
        return {
          url: endpoints.Training.EXERCISES_DETAILED(itemId),
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result, error, id) => {
        return [{ type: TRAININGS.EXERCISES, id }];
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    createEmptyTraining: builder.mutation<{ id: string }, void>({
      query() {
        return {
          url: endpoints.Training.EMPTY,
          method: HTTP_METHOD.POST,
        };
      },
      transformResponse(response: ITraining | { id: string }) {
        const id =
          response && typeof response === 'object' && 'id' in response
            ? (response as { id: string }).id
            : '';
        return { id };
      },
      invalidatesTags: (result, error) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        }
        const tags: Array<{ type: TRAININGS; id?: string }> = [
          {
            type: TRAININGS.TRAININGS,
            id: REDUX_CACHE.LIST,
          },
          {
            type: TRAININGS.TRAININGS_BY_PERIOD,
            id: REDUX_CACHE.LIST,
          },
          {
            type: TRAININGS.TRAININGS_BY_DAY,
            id: REDUX_CACHE.LIST,
          },
          {
            type: TRAININGS.TRAININGS_FOR_ANALYTIC,
            id: REDUX_CACHE.LIST,
          },
        ];
        if (result?.id) {
          tags.push({ type: TRAININGS.TRAININGS, id: result.id });
        }
        return tags;
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    startNewTraining: builder.mutation<
      { id: string },
      {
        body: ITraining;
      }
    >({
      query({ body }) {
        return {
          url: endpoints.Training.START_NEW,
          method: HTTP_METHOD.POST,
          body,
        };
      },
      transformResponse(response: ITraining | { id: string }) {
        const id =
          response && typeof response === 'object' && 'id' in response
            ? (response as { id: string }).id
            : '';
        return { id };
      },
      invalidatesTags: (result, error) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        }
        const tags: Array<{ type: TRAININGS; id?: string }> = [
          {
            type: TRAININGS.TRAININGS,
            id: REDUX_CACHE.LIST,
          },
          {
            type: TRAININGS.TRAININGS_LAZY,
          },
          {
            type: TRAININGS.TRAININGS_BY_PERIOD,
            id: REDUX_CACHE.LIST,
          },
          {
            type: TRAININGS.TRAININGS_BY_DAY,
            id: REDUX_CACHE.LIST,
          },
          {
            type: TRAININGS.TRAININGS_FOR_ANALYTIC,
            id: REDUX_CACHE.LIST,
          },
        ];
        if (result?.id) {
          tags.push({
            type: TRAININGS.TRAININGS,
            id: result.id,
          });
        }
        return tags;
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    postPutTraining: builder.mutation<
      { id: string },
      { id?: string; body: ITraining }
    >({
      query({ id, body }) {
        return {
          url: endpoints.Training.ROOT + (id ? `/${id}` : ''),
          method: id ? HTTP_METHOD.PUT : HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: TRAININGS.TRAININGS,
              id: REDUX_CACHE.LIST,
            },
            { type: TRAININGS.TRAININGS_LAZY },
            {
              type: TRAININGS.CREATED_FOR_OTHERS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: TRAININGS.TRAININGS_BY_PERIOD,
              id: REDUX_CACHE.LIST,
            },
            {
              type: TRAININGS.TRAININGS_BY_DAY,
              id: REDUX_CACHE.LIST,
            },
            {
              type: TRAININGS.TRAININGS_FOR_ANALYTIC,
              id: REDUX_CACHE.LIST,
            },
            {
              type: TRAININGS.TRAININGS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    startTrainingApi: builder.mutation<void, string>({
      query(id) {
        return {
          url: endpoints.Training.START.replace('${0}', id),
          method: HTTP_METHOD.PUT,
        };
      },
      invalidatesTags: (result, error, id) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: TRAININGS.TRAININGS,
              id: REDUX_CACHE.LIST,
            },
            { type: TRAININGS.TRAININGS_LAZY },
            {
              type: TRAININGS.TRAININGS_BY_PERIOD,
              id: REDUX_CACHE.LIST,
            },
            {
              type: TRAININGS.TRAININGS_BY_DAY,
              id: REDUX_CACHE.LIST,
            },
            {
              type: TRAININGS.TRAININGS_FOR_ANALYTIC,
              id: REDUX_CACHE.LIST,
            },
            {
              type: TRAININGS.TRAININGS,
              id: id,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    finishTrainingApi: builder.mutation<
      void,
      {
        id: string;
        body: {
          comment: string;
          startDate?: Date | string | null;
          finishDate?: Date | string | null;
          filesToUpload?: IAppFile[];
          mode?: 'create' | 'update';
          title?: string;
          programCollectionId?: string;
        };
      }
    >({
      query({ id, body }) {
        return {
          url: endpoints.Training.FINISH.replace('${0}', id),
          method: HTTP_METHOD.PUT,
          body,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          removeStartedTrainingCache(arg.id);
        } catch {
          // ignore
        }
      },
      invalidatesTags: (result, error, { id }) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: TRAININGS.TRAININGS,
              id: REDUX_CACHE.LIST,
            },
            { type: TRAININGS.TRAININGS_LAZY },
            {
              type: TRAININGS.TRAININGS_BY_PERIOD,
              id: REDUX_CACHE.LIST,
            },
            {
              type: TRAININGS.TRAININGS_BY_DAY,
              id: REDUX_CACHE.LIST,
            },
            {
              type: TRAININGS.TRAININGS_FOR_ANALYTIC,
              id: REDUX_CACHE.LIST,
            },
            {
              type: TRAININGS.TRAININGS,
              id: id,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    aiTrainingApi: builder.mutation<
      { id: string },
      {
        prompt: string;
        providerId?: string;
        muscleGroupIds?: string[];
        categoryIds?: string[];
        equipmentIds?: string[];
        developedSkillIds?: string[];
      }
    >({
      query({
        prompt,
        providerId,
        muscleGroupIds,
        categoryIds,
        equipmentIds,
        developedSkillIds,
      }) {
        return {
          url: endpoints.Training.AI_TRAINING,
          method: HTTP_METHOD.POST,
          body: {
            prompt,
            ...(providerId && { providerId }),
            ...(muscleGroupIds?.length && { muscleGroupIds }),
            ...(categoryIds?.length && { categoryIds }),
            ...(equipmentIds?.length && { equipmentIds }),
            ...(developedSkillIds?.length && { developedSkillIds }),
          },
        };
      },
      invalidatesTags: (result, error) => {
        if (error) return [REDUX_CACHE.ABORT_INVALIDATION];
        return [
          { type: TRAININGS.TRAININGS, id: REDUX_CACHE.LIST },
          { type: TRAININGS.TRAININGS_LAZY },
          { type: TRAININGS.TRAININGS_BY_PERIOD, id: REDUX_CACHE.LIST },
          { type: TRAININGS.TRAININGS_BY_DAY, id: REDUX_CACHE.LIST },
          { type: TRAININGS.TRAININGS_FOR_ANALYTIC, id: REDUX_CACHE.LIST },
        ];
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    postTrainingSet: builder.mutation<
      ITrainingSet[],
      {
        trainingId: string;
        sets: ITrainingSet[];
      }
    >({
      query({ sets, trainingId }) {
        return {
          url: endpoints.Training.SETS(trainingId),
          method: HTTP_METHOD.POST,
          body: { sets },
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data: newSets } = await queryFulfilled;
          if (!newSets?.length || !arg.trainingId) return;

          dispatch(
            trainingsApi.util.updateQueryData(
              'getTrainingByIdApi',
              arg.trainingId,
              (draft) => {
                if (!draft || draft.id !== arg.trainingId) return;
                const existing = draft.trainingSets ?? [];
                const merged = [...existing, ...newSets].sort(
                  (a, b) =>
                    (a.setNumber ?? 0) - (b.setNumber ?? 0) ||
                    (a.orderNumber ?? 0) - (b.orderNumber ?? 0),
                );
                draft.trainingSets = merged;
                syncStartedTrainingSnapshotFromTraining(
                  current(draft) as ITraining,
                );
              },
            ),
          );
        } catch {
          // ignore
        }
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: TRAININGS.TRAININGS,
              id: params.trainingId,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    putTrainingSet: builder.mutation<void, IPutTrainingSet>({
      query({ exerciseId, trainingSetId, trainingId, setNumber }) {
        return {
          url: endpoints.Training.SETS(trainingId, trainingSetId),
          method: HTTP_METHOD.PUT,
          body: { exerciseId, setNumber },
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: TRAININGS.TRAININGS,
              id: params.trainingId,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    deleteTrainingSet: builder.mutation<
      void,
      { trainingId: string; trainingSetId: string }
    >({
      query({ trainingId, trainingSetId }) {
        return {
          url: endpoints.Training.SETS(trainingId, trainingSetId),
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: TRAININGS.TRAININGS,
              id: params.trainingId,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    putTrainingSetItem: builder.mutation<
      void,
      {
        trainingId: string;
        trainingSetId: string;
        trainingSetItemId: string;
        skipInvalidation?: boolean;
        body: Partial<ITrainingSetItem>;
      }
    >({
      query({ body, trainingId, trainingSetId, trainingSetItemId }) {
        return {
          url: endpoints.Training.SET_ITEM(
            trainingId,
            trainingSetId,
            trainingSetItemId,
          ),
          method: HTTP_METHOD.PUT,
          body,
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          trainingsApi.util.updateQueryData(
            'getTrainingByIdApi',
            arg.trainingId,
            (draft) => {
              if (!draft || draft.id !== arg.trainingId) return;
              const set = draft.trainingSets?.find(
                (s) => s.id === arg.trainingSetId,
              );
              const item = set?.trainingSetItems?.find(
                (i) => i.id === arg.trainingSetItemId,
              );
              if (item && arg.body) {
                Object.assign(item, arg.body);
              }
              syncStartedTrainingSnapshotFromTraining(
                current(draft) as ITraining,
              );
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          if (params.skipInvalidation) {
            return [];
          } else {
            return [
              {
                type: TRAININGS.TRAININGS,
                id: params.trainingId,
              },
            ];
          }
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    postTrainingSetItem: builder.mutation<
      void,
      {
        trainingId: string;
        trainingSetId: string;
        item: ITrainingSetItem;
      }
    >({
      query({ item, trainingId, trainingSetId }) {
        return {
          url: endpoints.Training.SET_ITEM(trainingId, trainingSetId),
          method: HTTP_METHOD.POST,
          body: item,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: TRAININGS.TRAININGS,
              id: params.trainingId,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    deleteTrainingSetItem: builder.mutation<
      void,
      { trainingId: string; trainingSetId: string; trainingSetItemId: string }
    >({
      query({ trainingId, trainingSetId, trainingSetItemId }) {
        return {
          url: endpoints.Training.SET_ITEM(
            trainingId,
            trainingSetId,
            trainingSetItemId,
          ),
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: TRAININGS.TRAININGS,
              id: params.trainingId,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    putTrainingSetItemStatus: builder.mutation<
      void,
      {
        trainingId: string;
        trainingSetId: string;
        trainingSetItemId: string;
        status: TrainingSetItemStatus;
      }
    >({
      query({ status, trainingId, trainingSetId, trainingSetItemId }) {
        return {
          url: endpoints.Training.ITEM_STATUS(
            trainingId,
            trainingSetId,
            trainingSetItemId,
          ),
          method: HTTP_METHOD.PUT,
          body: { status },
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: TRAININGS.TRAININGS,
              id: params.trainingId,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    deleteTraining: builder.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: `${endpoints.Training.ROOT}/${id}`,
          method: HTTP_METHOD.DELETE,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          removeStartedTrainingCache(arg.id);
        } catch {
          // ignore
        }
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: TRAININGS.TRAININGS,
              id: params.id,
            },
            {
              type: TRAININGS.TRAININGS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: TRAININGS.TRAININGS_BY_PERIOD,
              id: REDUX_CACHE.LIST,
            },
            {
              type: TRAININGS.TRAININGS_BY_DAY,
              id: REDUX_CACHE.LIST,
            },
            {
              type: TRAININGS.TRAININGS_FOR_ANALYTIC,
              id: REDUX_CACHE.LIST,
            },
            {
              type: TRAININGS.CREATED_FOR_OTHERS,
              id: REDUX_CACHE.LIST,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    shareTrainingUsers: builder.mutation<
      void,
      { trainingId: string; add: string[]; remove?: string[] }
    >({
      query({ trainingId, add, remove = [] }) {
        return {
          url: endpoints.Training.SHARE_USERS(trainingId),
          method: HTTP_METHOD.POST,
          body: { add, remove },
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: TRAININGS.TRAININGS,
              id: REDUX_CACHE.LIST,
            },
            { type: TRAININGS.TRAININGS_LAZY },
            {
              type: TRAININGS.AVAILABLE,
              id: params.trainingId,
            },
            {
              type: TRAININGS.TRAININGS,
              id: params.trainingId,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    unshareTraining: builder.mutation<
      void,
      { trainingId: string; userId: string }
    >({
      query({ trainingId, userId }) {
        return {
          url: endpoints.Training.USER.replace('${0}', trainingId).replace(
            '${1}',
            userId,
          ),
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: TRAININGS.TRAININGS,
              id: REDUX_CACHE.LIST,
            },
            { type: TRAININGS.TRAININGS_LAZY },
            {
              type: TRAININGS.AVAILABLE,
              id: params.trainingId,
            },
            {
              type: TRAININGS.CREATED_FOR_OTHERS,
              id: params.trainingId,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    copyTraining: builder.mutation<
      ITraining,
      { trainingId: string; title: string; planedStartDate?: Date | null }
    >({
      query({ trainingId, title, planedStartDate }) {
        return {
          url: endpoints.Training.COPY(trainingId),
          method: HTTP_METHOD.POST,
          body: { title, planedStartDate },
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: TRAININGS.TRAININGS,
              id: REDUX_CACHE.LIST,
            },
            { type: TRAININGS.TRAININGS_LAZY },
            {
              type: TRAININGS.CREATED_FOR_OTHERS,
              id: REDUX_CACHE.LIST,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    bulkDeleteTrainingSets: builder.mutation<
      void,
      {
        trainingId: string;
        setNumber: number;
        orderNumber?: number;
      }
    >({
      query({ trainingId, setNumber, orderNumber }) {
        const queryParams = queryString.stringify({
          setNumber,
          ...(orderNumber !== undefined && { orderNumber }),
        });
        return {
          url: `${endpoints.Training.SETS(trainingId)}?${queryParams}`,
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: TRAININGS.TRAININGS,
              id: params.trainingId,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    bulkUpdateTrainingSets: builder.mutation<
      void,
      {
        trainingId: string;
        sets: ITrainingSet[];
      }
    >({
      query({ trainingId, sets }) {
        return {
          url: endpoints.Training.SETS(trainingId),
          method: HTTP_METHOD.PUT,
          body: { sets },
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          trainingsApi.util.updateQueryData(
            'getTrainingByIdApi',
            arg.trainingId,
            (draft) => {
              if (!draft || draft.id !== arg.trainingId) return;
              draft.trainingSets = mergeTrainingSetsForBulkUpdate(
                draft.trainingSets,
                arg.sets,
              );
              syncStartedTrainingSnapshotFromTraining(
                current(draft) as ITraining,
              );
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: TRAININGS.TRAININGS,
              id: params.trainingId,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    patchTrainingSetsOrder: builder.mutation<
      ITrainingSet[],
      {
        trainingId: string;
        sets: { id: string; setNumber: number; orderNumber: number }[];
      }
    >({
      query({ trainingId, sets }) {
        return {
          url: endpoints.Training.SETS_ORDER(trainingId),
          method: HTTP_METHOD.PATCH,
          body: { sets },
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data == null) return;
          dispatch(
            trainingsApi.util.updateQueryData(
              'getTrainingByIdApi',
              arg.trainingId,
              (draft) => {
                if (!draft || draft.id !== arg.trainingId) return;
                draft.trainingSets = data;
                syncStartedTrainingSnapshotFromTraining(
                  current(draft) as ITraining,
                );
              },
            ),
          );
        } catch {
          // ignore
        }
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        }
        return [
          {
            type: TRAININGS.TRAININGS,
            id: params.trainingId,
          },
        ];
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    putTrainingSetLoad: builder.mutation<
      void,
      { trainingId: string; setId: string; manualLoad: number | null }
    >({
      query({ trainingId, setId, manualLoad }) {
        return {
          url: endpoints.Training.SET_LOAD(trainingId, setId),
          method: HTTP_METHOD.PUT,
          body: { manualLoad },
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: TRAININGS.TRAININGS,
              id: params.trainingId,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
  }),
});

export const resetTrainingsApiState = () => trainingsApi.util.resetApiState();

const {
  useGetAvailableUserTrainingsApiQuery: useGetAvailableUserTrainings,
  useGetCreatedForOthersUserTrainingsApiQuery: useGetCreatedForOthersTrainings,
  useGetUserTrainingsApiQuery: useGetUserTrainings,
  useLazyGetUserTrainingsLazyApiQuery: useLazyGetUserTrainingsLazy,
  useGetTrainingsByDayApiQuery: useGetTrainingsByDay,
  useGetTrainingsByPeriodApiQuery: useGetTrainingsByPeriod,
  useGetTrainingsForAnalyticApiQuery: useGetTrainingsForAnalytic,
  useGetTrainingActivityTypesApiQuery: useGetTrainingActivityTypes,
  useGetTrainingByIdApiQuery,
  useLazyGetTrainingByIdApiQuery,
  usePostPutTrainingMutation: usePostPutTraining,
  useStartTrainingApiMutation: useStartTraining,
  useAiTrainingApiMutation: useAiTraining,
  useFinishTrainingApiMutation: useFinishTraining,
  usePutTrainingSetItemMutation: usePutTrainingSetItem,
  usePutTrainingSetItemStatusMutation: usePutTrainingSetItemStatus,
  useDeleteTrainingMutation: useDeleteTraining,
  useShareTrainingUsersMutation: useShareTrainingUsers,
  useUnshareTrainingMutation: useUnshareTraining,
  useCopyTrainingMutation: useCopyTraining,
  usePostTrainingSetMutation: usePostTrainingSet,
  usePutTrainingSetMutation: usePutTrainingSet,
  usePostTrainingSetItemMutation: usePostTrainingSetItem,
  useDeleteTrainingSetMutation: useDeleteTrainingSet,
  useDeleteTrainingSetItemMutation: useDeleteTrainingSetItem,
  useCreateEmptyTrainingMutation: useCreateEmptyTraining,
  useStartNewTrainingMutation: useStartNewTraining,
  useGetTrainingExercisesApiQuery: useGetTrainingExercisesQuery,
  useLazyGetTrainingExercisesApiQuery: useLazyGetTrainingExercises,
  useGetTrainingExercisesDetailedApiQuery: useGetTrainingExercisesDetailedQuery,
  useLazyGetTrainingExercisesDetailedApiQuery:
    useLazyGetTrainingExercisesDetailed,
  useBulkDeleteTrainingSetsMutation: useBulkDeleteTrainingSets,
  useBulkUpdateTrainingSetsMutation: useBulkUpdateTrainingSets,
  usePatchTrainingSetsOrderMutation: usePatchTrainingSetsOrder,
  usePutTrainingSetLoadMutation: usePutTrainingSetLoad,
} = trainingsApi;

export function useGetTrainingById(
  id: string | undefined,
  options?: Parameters<typeof useGetTrainingByIdApiQuery>[1],
): ReturnType<typeof useGetTrainingByIdApiQuery> & {
  isPreloadedCached: boolean;
} {
  const skip = !id || options?.skip;
  const result = useGetTrainingByIdApiQuery(id!, { ...options, skip });
  const cached = id
    ? ApiResponseCache.get<ITraining>(getStartedTrainingCacheKey(id))
    : null;
  return {
    ...result,
    data: result.data ?? cached ?? undefined,
    isPreloadedCached: !!cached,
  };
}

export {
  useGetAvailableUserTrainings,
  useGetCreatedForOthersTrainings,
  useGetUserTrainings,
  useLazyGetUserTrainingsLazy,
  useGetTrainingsByDay,
  useGetTrainingsByPeriod,
  useGetTrainingsForAnalytic,
  useGetTrainingActivityTypes,
  useGetTrainingByIdApiQuery,
  useLazyGetTrainingByIdApiQuery as useLazyGetTrainingById,
  usePostPutTraining,
  useStartTraining,
  useAiTraining,
  useFinishTraining,
  usePutTrainingSetItem,
  usePutTrainingSetItemStatus,
  useDeleteTraining,
  useShareTrainingUsers,
  useUnshareTraining,
  useCopyTraining,
  usePostTrainingSet,
  usePutTrainingSet,
  usePostTrainingSetItem,
  useDeleteTrainingSet,
  useDeleteTrainingSetItem,
  useCreateEmptyTraining,
  useStartNewTraining,
  useGetTrainingExercisesQuery,
  useLazyGetTrainingExercises,
  useGetTrainingExercisesDetailedQuery,
  useLazyGetTrainingExercisesDetailed,
  useBulkDeleteTrainingSets,
  useBulkUpdateTrainingSets,
  usePatchTrainingSetsOrder,
  usePutTrainingSetLoad,
};
